import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { apiClient, handleApiError } from '@/config/api';

export interface TimeOffRequest {
  id: number;
  employeeId: number;
  employeeName?: string;
  start: string;
  end: string;
  days: number;
  notes?: string;
  status: 'CREATED' | 'PENDING_APPROVAL' | 'APPROVED' | 'DENIED' | 'CANCELLED' | string;
  approvedByName?: string;
  approvedOn?: string | null;
  createdOn?: string;
  cancellable: boolean;
}

/** Minimal staff reference (camelCase from Spring; snake_case at the servlet). */
export interface StaffRef {
  id: number;
  firstName: string;
  lastName: string;
}

/**
 * Per-event coverage substitute. Fields arrive camelCase from Spring, which
 * translates the servlet's snake_case internally (see Phase 3 spec §0.6).
 */
export interface TimeOffSub {
  eventId: number;
  employee: StaffRef;
  acceptedOn?: string | null;
}

/** Most recent decline of a cover for an event (null/absent when never declined). */
export interface TimeOffDecline {
  employee: StaffRef;
  respondedOn?: string | null;
}

export interface TimeOffEvent {
  id: number;
  name: string;
  eventDate: string;
  start: string;
  end: string;
  dept?: string | null;
  type: string;
  status: string;
  sub?: TimeOffSub | null;
  /** Server-derived: 'unassigned' | 'assigned' | 'accepted'. Never computed client-side. */
  coverageState: string;
  lastDecline?: TimeOffDecline | null;
}

/** Why a checklist row can't be changed. */
export type TimeOffLockedReason = 'past' | 'other_request' | 'has_sub';

/** One row of the "which classes need a sub" checklist (Phase 2). */
export interface TimeOffCandidate extends TimeOffEvent {
  /** Already marked as needing a sub on THIS request. */
  selected: boolean;
  /** Marked by a different request — never selectable. */
  otherRequestId?: number | null;
  selectable: boolean;
  lockedReason?: TimeOffLockedReason | string | null;
}

export interface TimeOffCandidates {
  requestId: number;
  start: string;
  end: string;
  status: string;
  candidates: TimeOffCandidate[];
  /** Last date the employee's schedule is published through (explains an empty list). */
  schedulePublishedThrough?: string | null;
}

/** An overlapping shift a possible sub already teaches that day. */
export interface SubConflict {
  eventId: number;
  name: string;
  start: string;
  end: string;
}

/** One ranked sub option for a class (Phase 3). */
export interface SubCandidate {
  employee: StaffRef;
  /** true: works at the class's location; false: only the other one; absent: unknown. */
  sameLocation?: boolean | null;
  conflict?: SubConflict | null;
  rank: number;
}

/** Ranked sub picker list: everyone active except the requester, best fits first. */
export interface SubCandidates {
  event: TimeOffEvent;
  /** 'SL' | 'RM' | absent when unknown. */
  eventLocation?: string | null;
  candidates: SubCandidate[];
}

export interface SaveClassesPayload {
  id: number;
  /** Newly checked — POST /timeoff/{id}/events. */
  add: number[];
  /** Newly unchecked — DELETE /timeoff/{id}/events/{eventId} each. */
  remove: number[];
}

export interface SaveClassesResult {
  rejected: { eventId: number; reason: string }[];
}

export interface SubmitPayload {
  id: number;
  /** Confirms the requester has no classes in the window (empty checklist). */
  noClasses?: boolean;
}

/** Load failure with the HTTP status kept (403 = no longer allowed to see it). */
interface LoadError {
  message: string;
  status?: number;
}

export interface TimeOffCoverage {
  total: number;
  covered: number;
  accepted: number;
  gaps: number;
}

export interface TimeOffDetail extends TimeOffRequest {
  /** Request owner — the servlet's authoritative StaffRef (used to exclude the requester from the sub picker). */
  employee: StaffRef;
  events: TimeOffEvent[];
  coverage: TimeOffCoverage;
  approvedOn?: string | null;
  approvedBy?: StaffRef | null;
}

export interface ActionResponse {
  success: boolean;
  message: string;
  request?: TimeOffRequest;
}

export interface CreateTimeOffPayload {
  start: string;
  end: string;
  notes?: string;
  /**
   * When true, the backend leaves the request as a CREATED draft for the
   * assemble flow (pick classes → assign subs → submit). The assemble flow always
   * sets this. Omitting it makes the backend create-then-submit (the old
   * one-shot behavior), so this slice stays compatible with any caller that
   * doesn't assemble.
   */
  draft?: boolean;
}

export interface AssignSubPayload {
  id: number;
  eventId: number;
  subEmployeeId: number;
}

export interface ClearSubPayload {
  id: number;
  eventId: number;
}

/** Sub owner responding to a cover request (accept / decline). */
export interface RespondSubPayload {
  id: number;
  eventId: number;
}

interface TimeOffState {
  myRequests: TimeOffRequest[];
  pendingRequests: TimeOffRequest[];
  detail: TimeOffDetail | null;
  /** HTTP status of the last failed detail load (e.g. 403 when access was removed). */
  detailErrorStatus: number | null;
  candidates: TimeOffCandidates | null;
  isSavingClasses: boolean;
  isApprover: boolean;
  isLoading: boolean;
  isSubmitting: boolean;
  isAssigning: boolean;
  isResponding: boolean;
  error: string | null;
}

const initialState: TimeOffState = {
  myRequests: [],
  pendingRequests: [],
  detail: null,
  detailErrorStatus: null,
  candidates: null,
  isSavingClasses: false,
  isApprover: false,
  isLoading: false,
  isSubmitting: false,
  isAssigning: false,
  isResponding: false,
  error: null,
};

export const getMyTimeOff = createAsyncThunk('timeOff/getMine', async (_, { rejectWithValue }) => {
  try {
    return await apiClient.get<TimeOffRequest[]>('/timeoff/mine');
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});

export const createTimeOff = createAsyncThunk(
  'timeOff/create',
  async (payload: CreateTimeOffPayload, { rejectWithValue }) => {
    try {
      return await apiClient.post<ActionResponse>('/timeoff', payload);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

/** Read the full request + its selected classes + server-derived coverage. */
const statusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } } | undefined)?.response?.status;

export const getTimeOffDetail = createAsyncThunk<TimeOffDetail, number, { rejectValue: LoadError }>(
  'timeOff/getDetail',
  async (id, { rejectWithValue }) => {
    try {
      return await apiClient.get<TimeOffDetail>(`/timeoff/${id}`);
    } catch (error) {
      return rejectWithValue({ message: handleApiError(error), status: statusOf(error) });
    }
  }
);

/**
 * The requester's classes in the request window, each flagged selected /
 * selectable / locked. Read-only — builds the "which classes need a sub"
 * checklist. Replaces enumerate (which stamped everything).
 */
export const getTimeOffCandidates = createAsyncThunk(
  'timeOff/getCandidates',
  async (id: number, { rejectWithValue }) => {
    try {
      return await apiClient.get<TimeOffCandidates>(`/timeoff/${id}/candidates`);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

/**
 * Ranked sub candidates for one class (same location first, then no conflicting
 * shift). Requester or admin only. The picker keeps the result locally.
 */
export const getSubCandidates = createAsyncThunk(
  'timeOff/getSubCandidates',
  async ({ id, eventId }: { id: number; eventId: number }, { rejectWithValue }) => {
    try {
      return await apiClient.get<SubCandidates>(`/timeoff/${id}/events/${eventId}/sub-candidates`);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

/**
 * Save the checklist: POST the newly checked classes, DELETE the newly
 * unchecked ones, then reload detail (the per-class sub picker's source).
 * Resolves with any classes the server refused to mark (e.g. someone else's).
 */
export const saveTimeOffClasses = createAsyncThunk(
  'timeOff/saveClasses',
  async ({ id, add, remove }: SaveClassesPayload, { dispatch, rejectWithValue }) => {
    try {
      let rejected: SaveClassesResult['rejected'] = [];
      if (add.length > 0) {
        const res = await apiClient.post<{ rejected?: SaveClassesResult['rejected'] }>(
          `/timeoff/${id}/events`,
          { eventIds: add }
        );
        rejected = res?.rejected ?? [];
      }
      for (const eventId of remove) {
        try {
          await apiClient.delete(`/timeoff/${id}/events/${eventId}`);
        } catch (error) {
          // 404 = not selected on this request any more (e.g. removed by an
          // earlier, partly failed save) — the goal is met, keep going.
          if (statusOf(error) !== 404) {
            throw error;
          }
        }
      }
      await dispatch(getTimeOffDetail(id));
      return { rejected } as SaveClassesResult;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

/**
 * Assign a substitute to one event. The optimistic chip the screen may show is
 * never treated as final — we re-fetch detail so coverage reflects the server.
 */
export const assignSub = createAsyncThunk(
  'timeOff/assignSub',
  async ({ id, eventId, subEmployeeId }: AssignSubPayload, { dispatch, rejectWithValue }) => {
    try {
      const result = await apiClient.post<ActionResponse>(
        `/timeoff/${id}/events/${eventId}/sub`,
        { subEmployeeId }
      );
      await dispatch(getTimeOffDetail(id));
      return result;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

/** Remove the assigned substitute from an event, then reconcile detail. */
export const clearSub = createAsyncThunk(
  'timeOff/clearSub',
  async ({ id, eventId }: ClearSubPayload, { dispatch, rejectWithValue }) => {
    try {
      const result = await apiClient.delete<ActionResponse>(
        `/timeoff/${id}/events/${eventId}/sub`
      );
      await dispatch(getTimeOffDetail(id));
      return result;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

/**
 * The assigned sub ACCEPTS a cover request. Identity is server-resolved from
 * the JWT — the client sends no body. On success we re-fetch detail so any
 * open screen reflects the server's coverage state. The forward path can
 * resolve with `success:false` (servlet 409 already-responded / 403 not-the-sub)
 * — callers MUST inspect the payload, not just fulfilled-vs-rejected.
 */
export const acceptSub = createAsyncThunk(
  'timeOff/acceptSub',
  async ({ id, eventId }: RespondSubPayload, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiClient.post<ActionResponse>(`/timeoff/${id}/events/${eventId}/accept`);
      await dispatch(getTimeOffDetail(id));
      return res;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

/** The assigned sub DECLINES — the shift reverts to the requester to reassign. */
export const declineSub = createAsyncThunk(
  'timeOff/declineSub',
  async ({ id, eventId }: RespondSubPayload, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiClient.post<ActionResponse>(`/timeoff/${id}/events/${eventId}/decline`);
      await dispatch(getTimeOffDetail(id));
      return res;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

/**
 * Move a CREATED draft to PENDING_APPROVAL. Pass `{ id, noClasses: true }` to
 * confirm an empty class list (the server otherwise requires at least one).
 */
export const submitTimeOff = createAsyncThunk(
  'timeOff/submit',
  async (arg: number | SubmitPayload, { rejectWithValue }) => {
    const { id, noClasses } = typeof arg === 'number' ? { id: arg, noClasses: false } : arg;
    try {
      return noClasses
        ? await apiClient.post<ActionResponse>(`/timeoff/${id}/submit`, { noClasses: true })
        : await apiClient.post<ActionResponse>(`/timeoff/${id}/submit`);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const cancelTimeOff = createAsyncThunk(
  'timeOff/cancel',
  async (id: number, { rejectWithValue }) => {
    try {
      return await apiClient.post<ActionResponse>(`/timeoff/${id}/cancel`);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

/**
 * Loads the approval queue. The endpoint is admin-only — we read
 * `auth.user.employee.admin` first and short-circuit when the current user
 * isn't an admin, keeping the request out of the network log entirely. A
 * defensive 403 fallback stays in case the auth state is stale.
 */
export const getPendingTimeOff = createAsyncThunk(
  'timeOff/getPending',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as { auth?: { user?: { employee?: { admin?: boolean } } } };
    const isAdmin = !!state.auth?.user?.employee?.admin;
    if (!isAdmin) {
      return { isApprover: false, requests: [] as TimeOffRequest[] };
    }
    try {
      const data = await apiClient.get<TimeOffRequest[]>('/timeoff/pending');
      return { isApprover: true, requests: data };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        return { isApprover: false, requests: [] as TimeOffRequest[] };
      }
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const approveTimeOff = createAsyncThunk(
  'timeOff/approve',
  async (id: number, { rejectWithValue }) => {
    try {
      return await apiClient.post<ActionResponse>(`/timeoff/${id}/approve`);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const denyTimeOff = createAsyncThunk(
  'timeOff/deny',
  async (id: number, { rejectWithValue }) => {
    try {
      return await apiClient.post<ActionResponse>(`/timeoff/${id}/deny`);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

const timeOffSlice = createSlice({
  name: 'timeOff',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearDetail: (state) => {
      state.detail = null;
      state.candidates = null;
      state.detailErrorStatus = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMyTimeOff.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getMyTimeOff.fulfilled, (state, action) => {
        state.isLoading = false;
        state.myRequests = action.payload;
      })
      .addCase(getMyTimeOff.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getPendingTimeOff.pending, (state) => {
        // Clear a stale error so a prior network failure doesn't linger over a retry.
        state.error = null;
      })
      .addCase(getPendingTimeOff.fulfilled, (state, action) => {
        state.isApprover = action.payload.isApprover;
        state.pendingRequests = action.payload.requests;
        state.error = null;
      })
      .addCase(getPendingTimeOff.rejected, (state, action) => {
        // 403 is handled inside the thunk (returns isApprover:false); this
        // covers non-403 failures (network/5xx) so they surface, not swallow.
        state.error = action.payload as string;
      });

    builder
      .addCase(createTimeOff.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(createTimeOff.fulfilled, (state) => {
        state.isSubmitting = false;
      })
      .addCase(createTimeOff.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload as string;
      });

    // Detail — the source of truth for coverage.
    builder
      .addCase(getTimeOffDetail.pending, (state) => {
        state.error = null;
        state.detailErrorStatus = null;
      })
      .addCase(getTimeOffDetail.fulfilled, (state, action) => {
        state.detail = action.payload;
      })
      .addCase(getTimeOffDetail.rejected, (state, action) => {
        state.error = action.payload?.message ?? action.error.message ?? 'Something went wrong';
        state.detailErrorStatus = action.payload?.status ?? null;
      });

    // Class checklist (Phase 2).
    builder
      .addCase(getTimeOffCandidates.pending, (state) => {
        // Drop the previous list so nothing seeds checkboxes from stale `selected` flags.
        state.candidates = null;
        state.error = null;
      })
      .addCase(getTimeOffCandidates.fulfilled, (state, action) => {
        state.candidates = action.payload;
      })
      .addCase(getTimeOffCandidates.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(saveTimeOffClasses.pending, (state) => {
        state.isSavingClasses = true;
      })
      .addCase(saveTimeOffClasses.fulfilled, (state) => {
        state.isSavingClasses = false;
      })
      .addCase(saveTimeOffClasses.rejected, (state) => {
        // Surfaced by the screen as an Alert; the checklist stays usable.
        state.isSavingClasses = false;
      });

    // Assign / clear sub — reconciled via getTimeOffDetail dispatched in-thunk.
    builder
      .addCase(assignSub.pending, (state) => {
        state.isAssigning = true;
        state.error = null;
      })
      .addCase(assignSub.fulfilled, (state) => {
        state.isAssigning = false;
      })
      .addCase(assignSub.rejected, (state, action) => {
        state.isAssigning = false;
        state.error = action.payload as string;
      })
      .addCase(clearSub.pending, (state) => {
        state.isAssigning = true;
        state.error = null;
      })
      .addCase(clearSub.fulfilled, (state) => {
        state.isAssigning = false;
      })
      .addCase(clearSub.rejected, (state, action) => {
        state.isAssigning = false;
        state.error = action.payload as string;
      });

    // Accept / decline a cover request — reconciled via getTimeOffDetail.
    builder
      .addCase(acceptSub.pending, (state) => {
        state.isResponding = true;
        state.error = null;
      })
      .addCase(acceptSub.fulfilled, (state) => {
        state.isResponding = false;
      })
      .addCase(acceptSub.rejected, (state, action) => {
        state.isResponding = false;
        state.error = action.payload as string;
      })
      .addCase(declineSub.pending, (state) => {
        state.isResponding = true;
        state.error = null;
      })
      .addCase(declineSub.fulfilled, (state) => {
        state.isResponding = false;
      })
      .addCase(declineSub.rejected, (state, action) => {
        state.isResponding = false;
        state.error = action.payload as string;
      });

    // Submit a draft.
    builder
      .addCase(submitTimeOff.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(submitTimeOff.fulfilled, (state) => {
        state.isSubmitting = false;
      })
      .addCase(submitTimeOff.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearDetail } = timeOffSlice.actions;
export default timeOffSlice.reducer;
